import { useEffect, useState } from "react";
import { api, inr, fmtDate } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Factory, Truck, FilePlus2, ReceiptText, MailWarning, Send } from "lucide-react";

export const ActionCenterStrips = () => {
  const [ac, setAc] = useState(null);
  const [sending, setSending] = useState("");
  useEffect(() => { api.get("/intel/action-center").then((r) => setAc(r.data)).catch(() => {}); }, []);
  const sendInvoiceEmail = async (m) => {
    setSending(m.id);
    try {
      const r = await api.post(`/dispatch/${m.id}/invoice-email`);
      toast.success(`Tax Invoice ${r.data.invoice_number} emailed to the customer`);
      setAc({ ...ac, missed_invoice_emails: ac.missed_invoice_emails.filter((x) => x.id !== m.id) });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send invoice email"); }
    setSending("");
  };
  if (!ac) return null;
  return (
    <>
      {ac.stuck_jobs.length > 0 && (
        <div className="border border-orange-100 border-l-4 border-l-orange-500 bg-white rounded-xl p-4 shadow-[0_2px_12px_rgba(15,23,42,0.04)]" data-testid="ac-stuck-jobs">
          <div className="flex items-center gap-2 text-orange-800 font-semibold text-sm mb-2.5">
            <Factory size={15} strokeWidth={1.5} /> Production jobs open beyond 7 days — {ac.stuck_jobs.length}
          </div>
          <div className="flex flex-wrap gap-2">
            {ac.stuck_jobs.map((j, i) => (
              <Link key={i} to="/production" className="text-xs bg-orange-50/60 border border-orange-200 rounded-md px-2.5 py-1.5 text-orange-800 hover:bg-orange-100 transition-colors duration-200">
                <span className="font-mono">{j.number}</span> · {j.customer_name} · <b>{j.days}d open</b> ({j.status})
              </Link>
            ))}
          </div>
        </div>
      )}
      {ac.undelivered.length > 0 && (
        <div className="border border-teal-100 border-l-4 border-l-teal-500 bg-white rounded-xl p-4 shadow-[0_2px_12px_rgba(15,23,42,0.04)]" data-testid="ac-undelivered">
          <div className="flex items-center gap-2 text-teal-800 font-semibold text-sm mb-2.5">
            <Truck size={15} strokeWidth={1.5} /> In transit over 7 days — confirm delivery ({ac.undelivered.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {ac.undelivered.map((d, i) => (
              <Link key={i} to="/dispatch" className="text-xs bg-teal-50/60 border border-teal-200 rounded-md px-2.5 py-1.5 text-teal-800 hover:bg-teal-100 transition-colors duration-200">
                <span className="font-mono">{d.order_number}</span>{d.customer_name ? <> · <b>{d.customer_name}</b></> : null} · LR {d.lr_number} · {d.transporter} · dispatched {fmtDate(d.dispatch_date)}
              </Link>
            ))}
          </div>
        </div>
      )}
      {(ac.payables_overdue || []).length > 0 && (
        <div className="border border-orange-100 border-l-4 border-l-orange-500 bg-white rounded-xl p-4 shadow-[0_2px_12px_rgba(15,23,42,0.04)]" data-testid="ac-payables-overdue">
          <div className="flex items-center gap-2 text-orange-800 font-semibold text-sm mb-2.5">
            <ReceiptText size={15} strokeWidth={1.5} /> Vendor bills past 60-day credit — pay now ({ac.payables_overdue.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {ac.payables_overdue.map((b) => (
              <Link key={b.id} to={`/payments/payables/vendor/${b.vendor_id}`} className="text-xs bg-orange-50/60 border border-orange-200 rounded-md px-2.5 py-1.5 text-orange-800 hover:bg-orange-100 transition-colors duration-200" data-testid={`ac-payable-overdue-${b.id}`}>
                <span className="font-mono">{b.number}</span> · <b>{b.vendor_name}</b> · {inr(b.due)} · due {fmtDate(b.due_date)}
              </Link>
            ))}
          </div>
        </div>
      )}
      {(ac.bills_pending || []).length > 0 && (
        <div className="border border-violet-100 border-l-4 border-l-violet-500 bg-white rounded-xl p-4 shadow-[0_2px_12px_rgba(15,23,42,0.04)]" data-testid="ac-bills-pending">
          <div className="flex items-center gap-2 text-violet-800 font-semibold text-sm mb-2.5">
            <ReceiptText size={15} strokeWidth={1.5} /> Vendor bill details pending — add bill number ({ac.bills_pending.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {ac.bills_pending.map((p, i) => (
              <Link key={i} to={`/purchase-orders?q=${encodeURIComponent(p.number)}`} className="text-xs bg-violet-50/60 border border-violet-200 rounded-md px-2.5 py-1.5 text-violet-800 hover:bg-violet-100 transition-colors duration-200" data-testid={`ac-bill-pending-${p.id}`}>
                <span className="font-mono">{p.number}</span> · {p.supplier_name}{p.customer_name ? <> · for <b>{p.customer_name}</b></> : null}{p.sales_order_number ? <> · {p.sales_order_number}</> : null} · {inr(p.total)}
              </Link>
            ))}
          </div>
        </div>
      )}
      {(ac.missed_invoice_emails || []).length > 0 && (
        <div className="border border-rose-100 border-l-4 border-l-rose-500 bg-white rounded-xl p-4 shadow-[0_2px_12px_rgba(15,23,42,0.04)]" data-testid="ac-missed-invoice-emails">
          <div className="flex items-center gap-2 text-rose-800 font-semibold text-sm mb-2.5">
            <MailWarning size={15} strokeWidth={1.5} /> Invoice email not sent to customer ({ac.missed_invoice_emails.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {ac.missed_invoice_emails.map((m) => (
              <div key={m.id} className="flex items-center gap-2 text-xs bg-rose-50/60 border border-rose-200 rounded-md px-2.5 py-1.5 text-rose-800" data-testid={`ac-missed-inv-${m.id}`}>
                <span><span className="font-mono">{m.order_number}</span>{m.customer_name ? <> · <b>{m.customer_name}</b></> : null} · Invoice <span className="font-mono">{m.invoice_number}</span></span>
                <button onClick={() => sendInvoiceEmail(m)} disabled={sending === m.id}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-rose-600 hover:bg-rose-700 rounded px-2 py-0.5 disabled:opacity-50"
                        data-testid={`ac-send-inv-email-${m.id}`}>
                  <Send size={10} /> {sending === m.id ? "Sending…" : "Send Now"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export const PredictedItemsLine = ({ c }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const createLead = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/intel/predicted-orders/${c.customer_id}/create-lead`);
      toast.success(`Lead ${r.data.lead_number} created for ${c.company_name}`);
      navigate(`/leads?q=${encodeURIComponent(r.data.lead_number)}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create lead"); }
    setBusy(false);
  };
  return (
    <div className="w-full flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 pt-1 border-t border-amber-200/60">
      {c.predicted_items?.length > 0 && (
        <span className="text-[11px] text-amber-800/90">
          Likely: {c.predicted_items.map((p) => `${p.product}${p.size ? ` ${p.size}` : ""} ~${p.qty} ${p.unit}`).join(" · ")}
          {c.est_value > 0 && <> · est <b>{inr(c.est_value)}</b></>}
        </span>
      )}
      <button onClick={createLead} disabled={busy}
              className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-[#3B82F6] hover:bg-[#2563EB] rounded-md px-2 py-1 disabled:opacity-50 ml-auto"
              data-testid={`predicted-lead-${c.customer_id}`}>
        <FilePlus2 size={11} /> Create Lead
      </button>
    </div>
  );
};
